// app/providers.tsx
'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react';
import React, { Suspense, useEffect } from 'react';
import { useCookieConsent } from '@/app/context/cookieConsentContext';
import { isPostHogDisabled, posthogPersistenceForConsent } from '@/app/utils/posthogClient';

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const { consentGiven, isMounted } = useCookieConsent();

  useEffect(() => {
    if (!isMounted || isPostHogDisabled()) return;

    posthog.set_config({
      persistence: posthogPersistenceForConsent(consentGiven),
    });
  }, [consentGiven, isMounted]);

  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      {children}
    </PHProvider>
  );
}

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthogClient = usePostHog();

  useEffect(() => {
    if (isPostHogDisabled()) return;
    if (pathname && posthogClient) {
      let url = window.origin + pathname;
      if (searchParams.toString()) {
        url = url + '?' + searchParams.toString();
      }

      posthogClient.capture('$pageview', { $current_url: url });
    }
  }, [pathname, searchParams, posthogClient]);

  return null;
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}
